import type { RealtimeChannel } from '@supabase/supabase-js';
import { Alert } from 'react-native';
import { create } from 'zustand';

import {
  supabase,
  createMultiplayerRealtimeChannel,
  joinRoomPresence,
  leaveRoomPresence,
  getPresenceState,
  onPresenceSync,
  sendBroadcast,
  onBroadcast,
  listenToActiveRooms,
  listenToPlayerStatus,
  fetchSessionByRoomId,
  createGameRoom,
  joinGameRoom,
  setPlayerReady,
  fetchActiveRooms,
  listenToRoom,
  startGameSession,
  submitGameAnswer,
  endMultiplayerGameSession,
  listenToSession,
  fetchAnswersBySessionId,
  listenToAnswers,
  CreateGameRoomParams,
  JoinGameRoomParams,
  SetPlayerReadyParams,
  StartGameSessionParams,
  SubmitGameAnswerParams,
  EndGameSessionParams
} from '../../shared/services/supabaseService';

import { useUserProfile } from './useStore';
import { uiText } from '../../shared/utils/uiText';

function deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;

  if (obj1 == null || typeof obj1 != 'object' ||
      obj2 == null || typeof obj2 != 'object') {
    return obj1 == obj2;
  }

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false;
    }
    return true;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

// Type for chat messages
export type ChatMessage = {
  id: string;
  user: string;
  user_id: string;
  text: string;
  timestamp: string;
  type?: 'text' | 'emoji' | 'system';
};

type ReactionMessage = {
  user_id: string;
  name?: string;
  reaction: string;
  timestamp: string;
};

function isChatMessage(payload: unknown): payload is ChatMessage {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const candidate = payload as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.user === 'string' &&
    typeof candidate.user_id === 'string' &&
    typeof candidate.text === 'string' &&
    typeof candidate.timestamp === 'string'
  );
}

function isTypingPayload(payload: unknown): payload is { user_id: string; typing: boolean } {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const candidate = payload as Record<string, unknown>;
  return typeof candidate.user_id === 'string' && typeof candidate.typing === 'boolean';
}

function isReactionMessage(payload: unknown): payload is ReactionMessage {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const candidate = payload as Record<string, unknown>;
  return (
    typeof candidate.user_id === 'string' &&
    typeof candidate.reaction === 'string' &&
    typeof candidate.timestamp === 'string' &&
    (candidate.name === undefined || typeof candidate.name === 'string')
  );
}

// Deprecated legacy definitions replaced with canonical types
export type GameRoom = import('../multi-player/types/multiplayer-core.types').GameRoom;
export type GamePlayer = import('../multi-player/types/multiplayer-core.types').GamePlayer;
/*
  family_id?: string; // Family group that owns this room
  family_name?: string; // Display name of the family (optional)
  id: string;
  code: string;
  status: 'waiting' | 'active' | 'finished';
  category_id: number;
  difficulty_id: number; // Changed from difficulty: string
  created_at: string;
  host_id: string; // host user ID
  public?: boolean; // new: public/private lobby
}
*/
// Legacy GameRoomPlayer is now alias to GamePlayer for compatibility
export type GameRoomPlayer = import('../multi-player/types/multiplayer-core.types').GamePlayer;
/*
  id: string;
  room_id: string;
  user_id: string;
  name: string;        // Primary display name from profiles.name
  avatar_url?: string;
  is_ready: boolean;
  joined_at: string;
  streak?: number;     // Optional streak from profile
  language?: string;   // Optional language preference
*/
export interface GameSession {
  id: string;
  room_id: string;
  started_at: string;
  ended_at?: string;
}
export interface GameAnswer {
  id: string;
  session_id: string;
  user_id: string;
  question_id: string;
  answer: string;
  is_correct: boolean;
  answered_at: string;
}

// Subscription status tracker for RealtimeChannel instances
let channelSubscriptionStatus = new WeakMap<any, boolean>();

// Structured result for joinRoom to avoid swallowing errors into global state
export type JoinRoomResult =
  | { success: true; room: GameRoom }
  | { success: false; reason: 'room_full' | 'not_authenticated' | 'unknown'; message: string };

interface MultiplayerState {
  resetMultiplayerState: () => void;
  // Channel management
  channel: RealtimeChannel | null;
  activeChannels: Record<string, RealtimeChannel>;
  addActiveChannel: (key: string, channel: RealtimeChannel) => void;
  removeActiveChannel: (key: string) => void;
  
  // Player presence and chat
  onlinePlayers: Record<string, { name?: string; avatar_url?: string }>;
  chatMessages: ChatMessage[];
  typingPlayers: Record<string, boolean>;
  reactions: Array<{
    user_id: string;
    name?: string;
    reaction: string;
    timestamp: string;
  }>;
  joinPresence: (room_id: string, user_id: string, profile: { name?: string; nickname?: string; avatar_url?: string }) => void;
  leavePresence: () => void;
  sendChat: (msgObj: ChatMessage) => void;
  sendTyping: (typing: boolean) => void;
  sendReaction: (reaction: string) => void;
  subscribePresenceAndBroadcast: (room_id: string) => void;
  cleanupChannels: () => void;

  listenAnswers: (session_id: string) => RealtimeChannel;
  listenPlayers: (room_id: string, onChange: () => void) => RealtimeChannel | null;
  listenPlayerStatus: (room_id: string, onPlayerChange: (payload: any) => void) => RealtimeChannel | null;
  listenActiveRooms: (onChange: () => void) => RealtimeChannel | null;
  rooms: GameRoom[];
  currentRoom: GameRoom | null;
  players: GameRoomPlayer[];
  currentSession: GameSession | null;
  answers: GameAnswer[];
  loading: boolean;
  error: string | null;
  fetchRoomById: (room_id: string) => Promise<void>;
  fetchRooms: () => Promise<void>;
  createRoom: (params: CreateGameRoomParams) => Promise<GameRoom | null>;
  quickMatch: () => Promise<GameRoom | null>; // auto-match
  joinRoom: (params: JoinGameRoomParams) => Promise<JoinRoomResult>;
  setPlayerReady: (params: SetPlayerReadyParams) => Promise<void>;
  setPlayers: (players: GameRoomPlayer[] | ((prev: GameRoomPlayer[]) => GameRoomPlayer[])) => void; // Add direct players update method
  fetchPlayers: (room_id: string) => Promise<void>;
  fetchSessionByRoomId: (room_id: string) => Promise<void>; // NEW: fetch session for late joiners
  listenRoom: (room_id: string, onChange: () => void) => RealtimeChannel | null;
  listenSession: (session_id: string, onChange: () => void) => RealtimeChannel;
  startGame: (params: StartGameSessionParams) => Promise<GameSession | null>;
  submitAnswer: (params: SubmitGameAnswerParams) => Promise<void>;
  endGame: (params: EndGameSessionParams) => Promise<void>;
}

export const useMultiplayerStore = create<MultiplayerState>((set, get) => ({
  // Reset all multiplayer state and clean up channels
  resetMultiplayerState: () => {
    // Clean up all active channels
    const { activeChannels } = get();
    Object.entries(activeChannels).forEach(([key, channel]) => {
      try {
        if (__DEV__) console.log(`[cleanupChannels] Unsubscribing from channel: ${key}`);
        channel?.unsubscribe();
      } catch (e) { /* ignore */ }
    });
    // Clear the active channels and WeakMap after attempting unsubscribe
    set({
      channel: null,
      activeChannels: {},
      onlinePlayers: {},
      chatMessages: [],
      typingPlayers: {},
      reactions: [],
      rooms: [],
      currentRoom: null,
      players: [],
      currentSession: null,
      answers: [],
      loading: false,
      error: null,
    });
    // Clear the WeakMap as well
    channelSubscriptionStatus = new WeakMap();
  },
  // Fetch the latest multiplayer session for a given room_id (for late joiners)
  fetchSessionByRoomId: async (room_id: string) => {
    set({ loading: true });
    try {
      const session = await fetchSessionByRoomId(room_id);
      if (session) {
        set({ currentSession: session, loading: false, error: null });
        if (__DEV__) console.log('[multiplayerStore] fetchSessionByRoomId: session set', session);
      } else {
        set({ error: 'No session found for this room', loading: false });
        if (__DEV__) console.warn('[multiplayerStore] fetchSessionByRoomId: No session found');
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      if (__DEV__) console.error('[multiplayerStore] fetchSessionByRoomId error:', error);
    }
  },
  // Fetch a single room by ID and update currentRoom
  fetchRoomById: async (room_id: string) => {
    set({ loading: true });
    try {
      const { data: room, error } = await supabase
        .from('multiplayer_game_rooms')
        .select('*')
        .eq('id', room_id)
        .single();
      if (__DEV__) console.log('[multiplayerStore] fetchRoomById fetched room:', room, 'error:', error);
      if (error || !room) {
        set({ error: error?.message || 'Room not found', loading: false });
        return;
      }
      // Only set state if data actually changed
      const prevRoom = get().currentRoom;
      const isEqual = prevRoom && deepEqual(prevRoom, room);
      if (!isEqual) {
        set({ currentRoom: room, loading: false, error: null });
      } else {
        set({ loading: false, error: null });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },
  channel: null,
  onlinePlayers: {},
  chatMessages: [],
  typingPlayers: {},
  reactions: [],
  rooms: [],
  currentRoom: null,
  players: [],
  currentSession: null,
  answers: [],
  loading: false,
  error: null,
  
  // Direct method to update players array (accepts array or updater function)
  setPlayers: (playersOrUpdater: GameRoomPlayer[] | ((prev: GameRoomPlayer[]) => GameRoomPlayer[])) => {
    set((state) => ({
      players: typeof playersOrUpdater === 'function'
        ? (playersOrUpdater as (prev: GameRoomPlayer[]) => GameRoomPlayer[])(state.players)
        : playersOrUpdater,
    }));
  },

  // Fetch all active/waiting rooms
  fetchRooms: async (publicOnly: boolean = false): Promise<void> => {
    set({ loading: true });
    try {
      const data = await fetchActiveRooms({ publicOnly });
      set({ rooms: data || [], loading: false, error: null });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  // Create a new game room
  createRoom: async (params: CreateGameRoomParams): Promise<GameRoom | null> => {
    const userProfile = useUserProfile.getState();
    const { id, avatar_url } = userProfile;
    // Get display name from profile with fallbacks
    const safeName = userProfile.name || 'Player';
    if (!id) {
      Alert.alert(uiText('errorTitle'), uiText('notAuthenticated'));
      return null;
    }
    set({ loading: true });
    try {
      // Explicitly construct the object for the service call
      const serviceParams: CreateGameRoomParams = {
        host_id: id, // from userProfile
        name: safeName, // from userProfile, primary identifier
        avatar_url: avatar_url || params.avatar_url || '', // from userProfile or incoming params
        category_id: params.category_id, // from incoming params
        difficulty_id: params.difficulty_id, // from incoming params
        public: params.public, // from incoming params, or undefined
        room_type: params.room_type, // Required property for room type
        // Pass through optional group association properties
        family_id: params.family_id,
        friends_group_id: params.friends_group_id,
        church_id: params.church_id,
        team_id: params.team_id,
        max_players: params.max_players,
      };
      const room = await createGameRoom(serviceParams);
      set({ currentRoom: room, loading: false, error: null });
      return room;
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return null;
    }
  },

  // Quick match: join first available public room or create one
  quickMatch: async (): Promise<GameRoom | null> => {
    const userProfile = useUserProfile.getState();
    const { id, avatar_url } = userProfile;
    // Use name as primary identifier with fallbacks
    const safeName = userProfile.name || 'Player';
    if (!id) {
      Alert.alert(uiText('errorTitle'), uiText('notAuthenticated'));
      return null;
    }
    set({ loading: true });
    try {
      // Fetch public waiting rooms
      const publicRooms = await fetchActiveRooms({ publicOnly: true });
      let room = publicRooms.find((r: GameRoom) => r.status === 'waiting');
      // Use default or hardcoded values for category_id and difficulty_id
      const category_id = 5; // TODO: let user choose (Miracles has all formats)
      const difficulty_id = 1; // TODO: let user choose, assuming 1 for 'easy'
      if (room) {
        // Join existing public room
        const result = await get().joinRoom({
          code: room.code,
          user_id: id,
          name: safeName,
          avatar_url: avatar_url || '',
        });
        if (result.success) {
          set({ currentRoom: result.room, loading: false, error: null });
          return result.room;
        }
        // Only fall through to create on recoverable capacity failures.
        // Auth, network, or unknown errors should surface to the caller.
        if (result.reason !== 'room_full') {
          set({ error: result.message, loading: false });
          return null;
        }
        // room_full: fall through to create a new room
      }
      // Create new public room (also reached on room_full join failure)
      {
        const newRoom = await get().createRoom({
          host_id: id,
          name: safeName,
          avatar_url: avatar_url || '',
          category_id,
          difficulty_id,
          public: true,
          room_type: 'friends'
        });
        set({ currentRoom: newRoom, loading: false, error: null });
        return newRoom;
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return null;
    }
  },

  // Join an existing game room
  joinRoom: async (params: JoinGameRoomParams): Promise<JoinRoomResult> => {
    // Ensure we have fresh profile data before joining
    await useUserProfile.getState().fetchProfile();
    const userProfile = useUserProfile.getState();
    const { id } = userProfile;
    
    // Get display name from profile with comprehensive fallbacks
    // Debug logging to understand profile data
    console.log('[joinRoom] Fresh userProfile data:', { 
      id: userProfile.id, 
      name: userProfile.name, 
      loading: userProfile.loading 
    });
    
    // Use actual profile name if available, otherwise create meaningful identifier
    const safeName = (userProfile.name && userProfile.name !== 'Player') 
      ? userProfile.name 
      : `User_${id?.slice(-4)}` || 'Anonymous Player';
    const safeAvatarUrl = userProfile.avatar_url || '';
    if (!id) {
      return { success: false, reason: 'not_authenticated', message: 'User is not authenticated.' };
    }
    set({ loading: true });
    try {
      const payload = {
        ...params,
        user_id: id,
        name: safeName,
        avatar_url: safeAvatarUrl
      };
      if (__DEV__) console.log('[joinRoom] Insert payload:', payload);
      if (__DEV__) console.log('[joinRoom] Current user_id:', id);
      const room = await joinGameRoom(payload);
      if (__DEV__) console.log('[joinRoom] Result room:', room);
      set({ currentRoom: room, loading: false, error: null });
      return { success: true, room };
    } catch (error: any) {
      const reason = error.message === 'Room is full' ? 'room_full' as const : 'unknown' as const;
      set({ error: error.message, loading: false });
      return { success: false, reason, message: error.message };
    }
  },

  // Set player ready/unready
  setPlayerReady: async (params: SetPlayerReadyParams): Promise<void> => {
    const { id } = useUserProfile.getState();
    if (!id) {
      Alert.alert(uiText('errorTitle'), uiText('notAuthenticated'));
      return;
    }
    set({ loading: true });
    try {
      // Use the provided user_id instead of overriding it
      if (__DEV__) console.log('[multiplayerStore] Setting player ready with params:', params);
      await setPlayerReady(params);
      if (__DEV__) console.log('[multiplayerStore] Player ready state updated successfully');
      set({ loading: false, error: null });
    } catch (error: any) {
      if (__DEV__) console.error('[multiplayerStore] Error setting player ready:', error);
      set({ error: error.message, loading: false });
    }
  },

  // Fetch players for a specific room
  fetchPlayers: async (room_id: string) => {
    if (!room_id) {
      if (__DEV__) console.error('[fetchPlayers] No room_id provided');
      set({ error: 'No room_id provided', loading: false });
      return;
    }

    set({ loading: true });
    try {
      const { data: playersData, error: playersError } = await supabase
        .from('multiplayer_game_room_players')
        .select('*, user_profiles(name, language, avatar_url, streak)')
        .eq('room_id', room_id)
        .order('joined_at', { ascending: true });

      if (playersError) throw playersError;

      if (playersData && playersData.length > 0) {
        if (__DEV__) console.log('[fetchPlayers] Raw players data from database:', playersData);
        
        const enhancedPlayers = playersData.map(player => {
          const profile = player.user_profiles;
          
          // Debug logging for each player
          if (__DEV__) {
            console.log(`[fetchPlayers] Processing player ${player.user_id}:`, {
              player_name: player.name,
              profile_exists: !!profile,
              profile_name: profile?.name,
              final_name_will_be: profile?.name || player.name || 'Player'
            });
          }
          
          return {
            ...player,
            name: profile?.name || player.name || 'Player',
            avatar_url: profile?.avatar_url || player.avatar_url,
            streak: profile?.streak || 0,
            language: profile?.language || 'en',
          } as GameRoomPlayer;
        });
        if (__DEV__) console.log('[fetchPlayers] Enhanced players with profiles:', enhancedPlayers);
        set({ players: enhancedPlayers, loading: false, error: null });
      } else {
        set({ players: [], loading: false, error: null });
      }
    } catch (error: any) {
      if (__DEV__) console.error('[fetchPlayers] Error:', error);
      set({ error: error.message, loading: false });
    }
  },

  // Track active channels
  activeChannels: {} as Record<string, RealtimeChannel>,

  // Clean up all active channels
  cleanupChannels: () => {
    const { activeChannels } = get();
    if (__DEV__) console.log(`[cleanupChannels] Cleaning up ${Object.keys(activeChannels).length} active channels`);
    
    // Unsubscribe from all channels
    Object.entries(activeChannels).forEach(([key, channel]) => {
      try {
        if (__DEV__) console.log(`[cleanupChannels] Unsubscribing from channel: ${key}`);
        channel?.unsubscribe();
      } catch (error) {
        if (__DEV__) console.error(`[cleanupChannels] Error unsubscribing from channel ${key}:`, error);
      }
    });
    
    // Clear the active channels
    set({ activeChannels: {} });
  },
  
  // Add a channel to active channels
  addActiveChannel: (key: string, channel: RealtimeChannel) => {
    const { activeChannels } = get();
    set({
      activeChannels: {
        ...activeChannels,
        [key]: channel
      }
    });
  },
  
  // Remove a channel from active channels
  removeActiveChannel: (key: string) => {
    const { activeChannels } = get();
    const newChannels = { ...activeChannels };
    delete newChannels[key];
    set({ activeChannels: newChannels });
  },

  // Listen to room changes
  listenRoom: (room_id: string, onChange: () => void): RealtimeChannel | null => {
    if (!room_id) {
      if (__DEV__) console.error('[listenRoom] No room_id provided');
      return null;
    }
    
    const channelKey = `multiplayer-room-${room_id}-changes`;
    
    try {
      const existingChannel = get().activeChannels[channelKey];
      if (existingChannel) {
        if (__DEV__) console.log(`[listenRoom] Reusing existing channel for room ${room_id}`);
        return existingChannel;
      }

      if (__DEV__) console.log(`[listenRoom] Creating new channel for room ${room_id}`);
      const channel = supabase.channel(channelKey, {
        config: {
          broadcast: { ack: true },
        },
      });

      channel
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'multiplayer_game_rooms',
            filter: `id=eq.${room_id}`,
          },
          () => {
            if (__DEV__) console.log(`[listenRoom] Change detected for room ${room_id}`);
            onChange();
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            if (__DEV__) console.log(`[listenRoom] Subscribed to room changes for room ${room_id}`);
          }
        });
      
      // Store the channel for cleanup
      get().addActiveChannel(channelKey, channel);
      
      return channel;
    } catch (error) {
      if (__DEV__) console.error(`[listenRoom] Error subscribing to room ${room_id}:`, error);
      return null;
    }
  },

  // Listen to player changes (Realtime)
  listenPlayers: function(room_id: string, onChange: () => void): RealtimeChannel | null {
    if (!room_id) {
      if (__DEV__) console.error('[listenPlayers] No room_id provided');
      return null;
    }
    
    const channelKey = `multiplayer-room-${room_id}-players`;
    
    try {
      // Reuse existing channel if it exists and is subscribed
      const existingChannel = get().activeChannels[channelKey];
      if (existingChannel) {
        if (__DEV__) console.log(`[listenPlayers] Reusing existing channel for room ${room_id}`);
        return existingChannel;
      }

      if (__DEV__) console.log(`[listenPlayers] Creating new channel for room ${room_id}`);
      const channel = supabase.channel(channelKey, {
        config: {
          broadcast: { ack: true },
        },
      });

      channel
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'multiplayer_game_room_players',
            filter: `room_id=eq.${room_id}`,
          },
          (payload) => {
            if (__DEV__) console.log(`[listenPlayers] Change detected for room ${room_id}`, payload.eventType);
            onChange();
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            if (__DEV__) console.log(`[listenPlayers] Subscribed to player changes for room ${room_id}`);
          }
        });

      if (!this.activeChannels) {
        this.activeChannels = {};
      }
      
      this.activeChannels[channelKey] = channel;
      return channel;
    } catch (error) {
      if (__DEV__) console.error(`[listenPlayers] Error subscribing to player changes for room ${room_id}:`, error);
      return null; // Return null instead of throwing to prevent app crashes
    }
  },
  
  // Listen to detailed player status changes (Realtime)
  listenPlayerStatus: function(room_id: string, onPlayerChange: (payload: any) => void): RealtimeChannel | null {
    if (!room_id) {
      if (__DEV__) console.error('[listenPlayerStatus] No room_id provided');
      return null;
    }
    
    const channelKey = `multiplayer-room-${room_id}-player-status`;
    
    try {
      // Reuse existing channel if it exists and is subscribed
      const existingChannel = get().activeChannels[channelKey];
      if (existingChannel) {
        if (__DEV__) console.log(`[listenPlayerStatus] Reusing existing channel for room ${room_id}`);
        return existingChannel;
      }

      if (__DEV__) console.log(`[listenPlayerStatus] Creating new channel for room ${room_id}`);
      const channel = listenToPlayerStatus(room_id, onPlayerChange);
      
      if (!this.activeChannels) {
        this.activeChannels = {};
      }
      
      if (channel) {
        this.activeChannels[channelKey] = channel;
      }
      
      return channel;
    } catch (error) {
      if (__DEV__) console.error(`[listenPlayerStatus] Error subscribing to player status for room ${room_id}:`, error);
      return null; // Return null instead of throwing to prevent app crashes
    }
  },
  // Listen to active rooms in the lobby (Realtime)
  listenActiveRooms: function(onChange: () => void): RealtimeChannel | null {
    const channelKey = 'multiplayer-active-rooms';
    
    try {
      // Initialize activeChannels if it doesn't exist
      const state = get();
      if (!state.activeChannels) {
        set({ activeChannels: {} });
      }
      
      // Reuse existing channel if it exists and is subscribed
      const existingChannel = state.activeChannels?.[channelKey];
      if (existingChannel) {
        if (__DEV__) console.log('[listenActiveRooms] Reusing existing channel for active rooms');
        return existingChannel;
      }

      if (__DEV__) console.log('[listenActiveRooms] Creating new channel for active rooms');
      
      // Create the channel using the Supabase service - use the simplest approach possible
      const channel = listenToActiveRooms(() => {
        if (__DEV__) console.log('[listenActiveRooms] Change detected for active rooms');
        // Just call the original callback - let the component handle the data refresh
        onChange();
      });
      
      // Store the channel for cleanup using Zustand store
      const currentState = get();
      const updatedChannels = { ...currentState.activeChannels };
      
      if (channel) {
        updatedChannels[channelKey] = channel;
        set({ activeChannels: updatedChannels });
      } else {
        if (__DEV__) console.warn('[listenActiveRooms] Failed to create channel');
      }
      
      return channel;
    } catch (error) {
      if (__DEV__) console.error('[listenActiveRooms] Error subscribing to active rooms:', error);
      return null; // Return null instead of throwing to prevent app crashes
    }
  },

  listenSession: function(session_id: string, onChange: () => void): RealtimeChannel {
    const channelKey = `multiplayer-session-${session_id}`;
    
    try {
      // Reuse existing channel if it exists and is subscribed
      const existingChannel = get().activeChannels[channelKey];
      if (existingChannel) {
        if (__DEV__) console.log(`[listenSession] Reusing existing channel for session ${session_id}`);
        return existingChannel;
      }

      if (__DEV__) console.log(`[listenSession] Creating new channel for session ${session_id}`);
      const channel = listenToSession(session_id, () => {
        if (__DEV__) console.log(`[listenSession] Change detected for session ${session_id}`);
        onChange();
      });
      
      this.activeChannels[channelKey] = channel;
      return channel;
    } catch (error) {
      if (__DEV__) console.error(`[listenSession] Error subscribing to session ${session_id}:`, error);
      throw error;
    }
  },

  // Listen to answers in real-time for a session and update state
  listenAnswers: (session_id: string) => {
    const channelKey = `multiplayer-answers-${session_id}`;
    
    // Clean up existing channel if it exists
    const existingChannel = get().activeChannels[channelKey];
    if (existingChannel) {
      if (__DEV__) console.log(`[listenAnswers] Cleaning up existing channel for answers ${session_id}`);
      existingChannel.unsubscribe();
    }

    const fetchAndSetAnswers = async () => {
      try {
        const answers = await fetchAnswersBySessionId(session_id);
        set({ answers });
      } catch (error) {
        if (__DEV__) console.error(`[listenAnswers] Error fetching answers for session ${session_id}:`, error);
      }
    };

    // Initial fetch
    fetchAndSetAnswers();
    
    // Create and subscribe to the channel
    const channel = listenToAnswers(session_id, fetchAndSetAnswers);
    
    // Store the channel for cleanup
    set((state) => ({
      activeChannels: {
        ...state.activeChannels,
        [channelKey]: channel,
      },
    }));
    
    return channel;
  },

  // Start a multiplayer game session
  startGame: async (params: StartGameSessionParams): Promise<GameSession | null> => {
    if (__DEV__) console.log('[multiplayerStore] startGame called with params:', params);
    set({ loading: true });
    try {
      const session = await startGameSession(params);
      if (__DEV__) console.log('[multiplayerStore] Game session created, updating state');
      set({ currentSession: session, loading: false, error: null });
      
      // Force a refetch of the room to ensure we have the latest status
      if (params.room_id) {
        await get().fetchRoomById(params.room_id);
      }
      
      return session;
    }
    catch (error: any) {
      if (__DEV__) console.error('[multiplayerStore] Error starting game:', error);
      set({ error: error.message, loading: false });
      return null;
    }
  },

  // Submit an answer
  submitAnswer: async (params: SubmitGameAnswerParams): Promise<void> => {
    const { id } = useUserProfile.getState();
    if (!id) {
      Alert.alert(uiText('errorTitle'), uiText('notAuthenticated'));
      return;
    }
    set({ loading: true });
    try {
      await submitGameAnswer({ ...params, user_id: id });
      set({ loading: false, error: null });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  // End a multiplayer game session
  endGame: async (params: EndGameSessionParams): Promise<void> => {
    set({ loading: true });
    try {
      await endMultiplayerGameSession(params);
      set({ loading: false, error: null });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  joinPresence: (room_id: string, user_id: string, profile: { name?: string; nickname?: string; avatar_url?: string }) => {
    const channel = get().channel || createMultiplayerRealtimeChannel(room_id);
    const enhancedProfile = {
      ...profile,
      name: profile.name || profile.nickname || '',
      nickname: profile.nickname || (profile.name || '')
    };
    joinRoomPresence(channel, user_id, enhancedProfile);
    set({ channel });
  },
  leavePresence: () => {
    const channel = get().channel;
    if (channel) {
      leaveRoomPresence(channel);
      set({ channel: null, onlinePlayers: {}, typingPlayers: {} });
    }
  },
  sendTyping: (typing: boolean) => {
    const channel = get().channel;
    const userProfile = useUserProfile.getState();
    if (channel && userProfile.id) {
      sendBroadcast(channel, 'typing', {
        user_id: userProfile.id,
        typing
      });
    }
  },
  sendChat: (msgObj: ChatMessage) => {
    const channel = get().channel;
    if (!msgObj || typeof msgObj !== 'object') return;
    if (channel && typeof msgObj.user_id === 'string') {
      sendBroadcast(channel, 'chat', msgObj);
    }
  },
  sendReaction: (reaction) => {
    const channel = get().channel;
    const userProfile = useUserProfile.getState();
    if (channel && userProfile.id) {
      sendBroadcast(channel, 'reaction', {
        user_id: userProfile.id,
        name: userProfile.name, // Add name field for consistency
        nickname: (userProfile as any).nickname ?? userProfile.name,
        reaction,
        timestamp: new Date().toISOString()
      });
    }
  },
  subscribePresenceAndBroadcast: (room_id) => {
    let channel = get().channel;
    if (!channel) {
      channel = createMultiplayerRealtimeChannel(room_id);
      set({ channel });
    }
    // Presence sync
    onPresenceSync(channel, (state) => {
      // Flatten state to user_id -> profile
      const onlinePlayers: Record<string, any> = {};
      Object.entries(state).forEach(([user_id, arr]) => {
        if (Array.isArray(arr) && arr.length > 0) {
          onlinePlayers[user_id] = arr[0];
        }
      });
      set({ onlinePlayers });
    });
    // Chat messages
    onBroadcast(channel, 'chat', (payload) => {
      const msg = payload.payload;
      if (isChatMessage(msg)) {
        set((s) => ({ chatMessages: [...s.chatMessages, msg] }));
      } else {
        if (__DEV__) console.warn('[multiplayerStore] Skipping malformed chat message:', msg);
      }
    });
    // Typing events
    onBroadcast(channel, 'typing', (payload) => {
      const typingPayload = payload.payload;
      if (isTypingPayload(typingPayload)) {
        set((s) => ({ typingPlayers: { ...s.typingPlayers, [typingPayload.user_id]: typingPayload.typing } }));
      }
    });
    // Reactions
    onBroadcast(channel, 'reaction', (payload) => {
      const reactionPayload = payload.payload;
      if (isReactionMessage(reactionPayload)) {
        set((s) => ({ reactions: [...s.reactions, reactionPayload] }));
      }
    });
    // Subscribe only if not already subscribed (tracked via WeakMap)
    if (!channelSubscriptionStatus.get(channel)) {
      channel.subscribe();
      channelSubscriptionStatus.set(channel, true);
    }
  },
}));
